import { MODE, normalizeMode } from './runContext.js';
import { toRequestSymbol } from './symbolFormat.js';

/**
 * Form-locking decisions for strategy policy (TASK-20260819 / ADR-0024 §2), as pure functions so the
 * interleavings are unit-testable without mounting a component.
 *
 * The backtest and deploy forms share these: the deploy form reuses the Backtesting components
 * verbatim, so the locking rules must not fork between them.
 */

/**
 * Message shown when a self-universe strategy is aimed at a run mode that cannot resolve its panel
 * (TASK-20260820-shishito-run-blockers; re-keyed from venue to mode by ADR-0040).
 *
 * It used to read "Not yet available for live or paper trading" (ADR-0024 §7), which was true when the
 * server refused every self-universe deploy. Deploy-time panel resolution has since shipped and Paper
 * mode is open, so that text now denies a capability the product has — the operator report that
 * started this task. It names the mode that DOES work: a blocked form without a way forward reads as
 * broken rather than as gated.
 */
export const SELF_UNIVERSE_DEPLOY_BLOCKED =
  'This strategy selects its own universe, which can only be deployed in Paper mode right now. '
  + 'Switch to Paper Trading to deploy it, or run a backtest.';

/**
 * Message shown when a self-universe strategy is aimed at a quote currency its regime input cannot read
 * (AI review of TASK-20260820-shishito-run-blockers).
 *
 * Both server boundaries refuse this — the deploy path and, as of Phase D, the backtest path — because
 * the regime layer reads the BTC-USD pair by name. Without it here, Paper + USDC passed the client gate
 * and was rejected only after the operator filled in and submitted the whole form: exactly the
 * pre-submit-message-to-post-submit-rejection downgrade that mirroring the server exists to prevent.
 */
export const SELF_UNIVERSE_QUOTE_BLOCKED =
  'This strategy must be deployed with USD as its quote currency — its market-regime input reads the '
  + 'BTC-USD pair specifically, so it would run without ever computing a regime.';

/**
 * How the symbol picker should render.
 * @param {{policyState: string, ownUniverse: boolean}} input
 * @returns {{locked: boolean, requiresSymbols: boolean}}
 */
export function symbolFieldState({ policyState, ownUniverse }) {
  // Only a LOADED policy may lock the field: locking on a guess would blank a legitimate selection.
  const locked = policyState === 'loaded' && ownUniverse === true;
  return { locked, requiresSymbols: !locked };
}

/**
 * Whether the form may submit, and why not when it may not. Fails closed on unknown policy: without it
 * we cannot tell whether symbols are required or forbidden, and either guess produces a request the
 * boundary rejects.
 *
 * For a self-universe DEPLOY the gate mirrors the server's run-mode allow-list (Paper only — ADR-0040:
 * self-universe = Paper), mode for mode. Mirroring is the whole point: a client that permits what the
 * server refuses downgrades a clear pre-submit message to a post-submit rejection, and a client that
 * refuses what the server permits hides a shipped feature — which is exactly the state this replaced.
 *
 * @param {{policyState: string, ownUniverse: boolean, isDeploy: boolean, mode?: string,
 *   quoteCurrency?: string}} input
 * @returns {{canSubmit: boolean, reason: string|null}}
 */
export function submitGate({ policyState, ownUniverse, isDeploy, mode, quoteCurrency }) {
  if (policyState !== 'loaded') {
    return { canSubmit: false, reason: 'Loading strategy configuration…' };
  }

  if (ownUniverse) {
    // Only PAPER mode resolves a panel today. Written as an allow-list, like the server's: a mode added
    // later is then excluded until someone decides otherwise, rather than admitted by omission — the
    // shape of the server-side gap this task also closed, where asking "is it live?" let the sandbox in.
    // The rule is about the MODE, not the venue (ADR-0040): Paper on Coinbase passes; Live on either
    // venue is refused.
    //
    // A caller that models NO mode is not asserting one we can refuse, so it passes and the server
    // arbitrates — the same "explain a refusal earlier, never invent one" policy the quote check below
    // follows. (A mode that IS supplied but is not Paper is refused, including an unrecognised one.)
    if (isDeploy && mode !== undefined && normalizeMode(mode) !== MODE.PAPER) {
      return { canSubmit: false, reason: SELF_UNIVERSE_DEPLOY_BLOCKED };
    }

    // The quote currency the regime input can actually read. This one is NOT deploy-only: the server
    // refuses a non-USD self-universe BACKTEST too (Phase D), so gating it on isDeploy would leave the
    // backtest form — the surface this task was reported against — showing an enabled Run button for a
    // request the server rejects after submission.
    if (quoteCurrency !== undefined && String(quoteCurrency).toUpperCase() !== 'USD') {
      return { canSubmit: false, reason: SELF_UNIVERSE_QUOTE_BLOCKED };
    }
  }

  return { canSubmit: true, reason: null };
}

/**
 * How the candle-frequency selector should render for a strategy (TASK-20260821).
 *
 * A pure function rather than inline component logic because there is no component-test harness in this
 * repo (AI review): inline, the `disabled` binding and the forced-value assignment would be asserted by
 * nothing at all and could regress green. Here the DECISION is pinned, and the components only bind it.
 *
 * Deliberately does NOT take the form's current value: the decision depends only on what the strategy
 * requires and what the control can render. Feeding the current value in created a reactive cycle in
 * Svelte (`frequencySeconds → frequencyField → frequencySeconds`), which fails the build — a good
 * signal that the current value was never an input to this decision in the first place.
 *
 * @param {{requiredFrequency: number|null, options?: number[]}} input
 *   `options` are the values the selector can actually render.
 * @returns {{locked: boolean, value: number|null}} whether to disable the control, and the value to
 *   force when locked (null when nothing is forced, so the caller leaves its own value alone)
 */
export function frequencyFieldState({ requiredFrequency, options }) {
  // Null means the strategy pins nothing — the operator's choice stands, exactly as before.
  if (requiredFrequency === null || requiredFrequency === undefined) {
    return { locked: false, value: null };
  }

  // Never force a value the <select> has no <option> for (AI review): a bound select with an unmatched
  // value renders BLANK while still submitting that value — a disabled, empty control that silently
  // sends something. Unreachable today (3600 is offered), but a future 4h/12h declaration would hit it,
  // so it fails visibly here instead. The server enforces the rule regardless, which is why leaving the
  // control usable is safe: the operator sees a real refusal rather than an empty box.
  if (Array.isArray(options) && !options.includes(requiredFrequency)) {
    return { locked: false, value: null };
  }

  return { locked: true, value: requiredFrequency };
}

/**
 * SizingAlgo enum ints, mirroring the C# enum (the API has no string-enum converter).
 * Kept here because the locked-value mapping below must speak the wire's language.
 */
const SIZING_ALGO = { EqualSplit: 0, InverseVol: 1, ATR: 2, StrategyWeights: 3 };

/**
 * Top-level form fields (NOT tuning entries) that a strategy can freeze, and how to turn the server's
 * declared value into the form's representation. `EnableStopOrders` and `SizingLogic` ride the request
 * itself, so they are ordinary form controls rather than `StrategyParameters` rows — which is exactly
 * why the first cut of the lock rendering missed them.
 *
 * @param {Array<{name: string, display: string}>} lockedParams
 * @returns {{enableStopOrders?: boolean, sizingLogic?: number}} values to force, keyed by form field
 */
export function lockedTopLevelValues(lockedParams) {
  const forced = {};
  for (const p of lockedParams ?? []) {
    if (p.name === 'EnableStopOrders') {
      // Every strategy that freezes stops freezes them OFF — its own exit rules are the exit engine.
      forced.enableStopOrders = false;
    } else if (p.name === 'SizingLogic') {
      // A strategy freezing its sizing owns the weights (`StrategyWeights`); the display text is prose,
      // so the value comes from the enum, not from parsing the label.
      forced.sizingLogic = SIZING_ALGO.StrategyWeights;
    }
  }
  return forced;
}

/**
 * The value a top-level field should hold after a strategy change. While a strategy freezes the field the form
 * forces its value; when the lock is RELEASED (the operator switches to a strategy that does not freeze it) the
 * forced value must not linger — the control has no option for it, so it would ride the request invisibly
 * (TASK-20261002: a moving-average backtest ran with KCP-1's 'Strategy weights' sizing and never traded).
 * Released → the form's default; otherwise the current value is kept.
 *
 * @template T
 * @param {{wasLocked: boolean, isLocked: boolean, current: T, fallback: T}} input
 * @returns {T}
 */
export function valueAfterLockRelease({ wasLocked, isLocked, current, fallback }) {
  return wasLocked && !isLocked ? fallback : current;
}

/** Whether a named top-level field is frozen by the strategy (so its control renders disabled). */
export function isTopLevelLocked(lockedParams, name) {
  return (lockedParams ?? []).some((p) => p.name === name);
}

/**
 * The value to SEND for a top-level field the strategy freezes. Null means "not specified" on the wire
 * (TASK-20260819 follow-up): the server then lets the strategy decide, rather than receiving a value the
 * operator never chose. Sending the forced value would also be accepted, but null is the honest signal —
 * the operator did not pick it, the strategy did.
 *
 * @param {Array<{name: string}>} lockedParams
 * @param {string} name - the declared parameter name (e.g. 'SizingLogic')
 * @param {*} formValue - what the form currently holds
 */
export function requestTopLevelValue(lockedParams, name, formValue) {
  return isTopLevelLocked(lockedParams, name) ? null : formValue;
}

/**
 * The request's `Symbols` value. A self-universe strategy sends none — the server resolves the panel
 * and rejects an explicit list (ADR-0024 §3) — regardless of anything stale in the picker.
 * @param {{ownUniverse: boolean, selected: string[]}} input
 */
export function requestSymbols({ ownUniverse, selected }) {
  if (ownUniverse) return '';

  const unique = [...new Set((selected ?? []).map((s) => s.trim()).filter((s) => s.length > 0))];
  return unique.map(toRequestSymbol).join(',');
}
